import { createReducer, Reducer } from "@reduxjs/toolkit";
import produce from "immer";
import { Pageable } from "../../services/pageable";
import { transformActionWithCountry } from "../../utils/utils";
import { RemoteResource } from "../common/fetchable";
import {
  EmptyFetchable,
  Fetchable,
  isFetchableError,
} from "../common/fetchable/helpers";
import { countries } from "../common/models";
import { VerificationActions } from "./actions";
import { VerificationDTO as AMLVerificationDTO } from "./models/dto/aml";
import { VerificationDTO as DueDiligenceVerificationDTO } from "./models/dto/due-diligence";
import { VerificationDTO as KYCVerificationDTO } from "./models/dto/kyc";

export namespace Verification {
  export const currentMerchantConfig: RemoteResource<
    KYCVerificationDTO.Responses.GetMerchantConfig.T,
    KYCVerificationDTO.Responses.GetMerchantConfig.T
  > = {
    fetch: VerificationActions.getCurrentMerchantConfig,
    updateStore: VerificationActions.setCurrentMerchantConfig,
    selector: (store) => store.verification.currentMerchant,
  };

  export const allKyc: RemoteResource<
    KYCVerificationDTO.Responses.GetAllKyc.T,
    KYCVerificationDTO.Responses.GetAllKyc.T,
    Pageable.T<
      | "firstName"
      | "lastName"
      | "country"
      | "emailAddress"
      | "userStatus"
      | "createdDate"
    >
  > = {
    fetch: VerificationActions.setAllKycPagination,
    updateStore: VerificationActions.setAllKyc,
    selector: (store) => store.verification.allKyc.data,
  };

  export const allSelfKyc: RemoteResource<
    KYCVerificationDTO.Responses.GetAllKyc.T,
    KYCVerificationDTO.Responses.GetAllKyc.T,
    Pageable.T<"userStatus" | "createdDate">
  > = {
    fetch: VerificationActions.setAllSelfKycPagination,
    updateStore: VerificationActions.setAllSelfKyc,
    selector: (store) => store.verification.allSelfKyc.data,
  };

  export const allKyb: RemoteResource<
    KYCVerificationDTO.Responses.GetAllKyb.T,
    KYCVerificationDTO.Responses.GetAllKyb.T,
    Pageable.T<keyof KYCVerificationDTO.Responses.GetSingleKyb.T>
  > = {
    fetch: VerificationActions.setAllKybPagination,
    updateStore: VerificationActions.setAllKyb,
    selector: (store) => store.verification.allKyb.data,
  };
  export const checksSummary: RemoteResource<
    KYCVerificationDTO.Responses.GetChecksSummary.T,
    KYCVerificationDTO.Responses.GetChecksSummary.T
  > = {
    fetch: VerificationActions.getChecksSummary,
    updateStore: VerificationActions.setChecksSummary,
    selector: (store) => store.verification.checksSummary,
  };
  // export const KYBJurisdictions: RemoteResource<
  //   KYCVerificationDTO.Responses.GetKYBJurisdictions.T,
  //   KYCVerificationDTO.Responses.GetKYBJurisdictions.T
  // > = {
  //   fetch: VerificationActions.getKYBJurisdictions,
  //   updateStore: VerificationActions.setKYBJurisdictions,
  //   selector: (store) => store.verification.KYBJurisdictions,
  // };
  export const allIndividualAml: RemoteResource<
    AMLVerificationDTO.Responses.GetAllIndividualAml.T,
    AMLVerificationDTO.Responses.GetAllIndividualAml.T,
    Pageable.T<
      Exclude<
        keyof AMLVerificationDTO.Responses.GetSingleIndividualAml.T,
        "ongoingChecks" | "fraudCheckId"
      >
    >
  > = {
    fetch: VerificationActions.setAllIndividualAmlPagination,
    updateStore: VerificationActions.setAllIndividualAml,
    selector: (store) => store.verification.allIndividualAml.data,
  };

  export const allBusinessAml: RemoteResource<
    AMLVerificationDTO.Responses.GetAllBusinessAml.T,
    AMLVerificationDTO.Responses.GetAllBusinessAml.T,
    Pageable.T<keyof AMLVerificationDTO.Responses.GetSingleBusinessAml.T>
  > = {
    fetch: VerificationActions.setAllBusinessAmlPagination,
    updateStore: VerificationActions.setAllBusinessAml,
    selector: (store) => store.verification.allBusinessAml.data,
  };

  export const allDueDiligenceConfig: RemoteResource<
    DueDiligenceVerificationDTO.Responses.DueDiligence.Config.GetAll.T,
    DueDiligenceVerificationDTO.Responses.DueDiligence.Config.GetAll.T,
    Pageable.T<
      keyof DueDiligenceVerificationDTO.Responses.DueDiligence.Config.Get.T
    >
  > = {
    fetch: VerificationActions.setAllDueDiligenceConfigPagination,
    updateStore: VerificationActions.setAllDueDiligenceConfig,
    selector: (store) => store.verification.allDueDiligenceConfig.data,
  };
}

/**
 * Interface used for Redux Store
 */
export interface Verification {
  checksSummary: Fetchable<KYCVerificationDTO.Responses.GetChecksSummary.T>;
  currentMerchant: Fetchable<KYCVerificationDTO.Responses.GetMerchantConfig.T>;
  KYBJurisdictions: Fetchable<KYCVerificationDTO.Responses.GetKYBJurisdictions.T>;
  allKyc: {
    data: Fetchable<KYCVerificationDTO.Responses.GetAllKyc.T>;
    pagination: Pageable.T<
      | "firstName"
      | "lastName"
      | "country"
      | "emailAddress"
      | "userStatus"
      | "createdDate"
    >;
  };

  allSelfKyc: {
    data: Fetchable<KYCVerificationDTO.Responses.GetAllKyc.T>;
    pagination: Pageable.T<"createdDate" | "userStatus">;
  };

  allKyb: {
    data: Fetchable<KYCVerificationDTO.Responses.GetAllKyb.T>;
    pagination: Pageable.T<keyof KYCVerificationDTO.Responses.GetSingleKyb.T>;
  };
  allIndividualAml: {
    data: Fetchable<AMLVerificationDTO.Responses.GetAllIndividualAml.T>;
    pagination: Pageable.T<
      Exclude<
        keyof AMLVerificationDTO.Responses.GetSingleIndividualAml.T,
        "ongoingChecks" | "fraudCheckId"
      >
    >;
  };
  allBusinessAml: {
    data: Fetchable<AMLVerificationDTO.Responses.GetAllBusinessAml.T>;
    pagination: Pageable.T<
      keyof AMLVerificationDTO.Responses.GetSingleBusinessAml.T
    >;
  };
  allDueDiligenceConfig: {
    data: Fetchable<DueDiligenceVerificationDTO.Responses.DueDiligence.Config.GetAll.T>;
    pagination: Pageable.T<
      Exclude<
        keyof DueDiligenceVerificationDTO.Responses.DueDiligence.Config.Get.T,
        "id" | "url"
      >
    >;
  };
  newDueDiligenceForm: Record<
    string,
    Partial<
      DueDiligenceVerificationDTO.Requests.DueDiligence.Form.Post.Init.T &
      DueDiligenceVerificationDTO.Requests.DueDiligence.Form.Post.Attachments.T &
      DueDiligenceVerificationDTO.Requests.DueDiligence.Form.Post.Final.T
    >
  >;
}

const initialState: Verification = {
  checksSummary: EmptyFetchable,
  currentMerchant: EmptyFetchable,
  KYBJurisdictions: EmptyFetchable,
  allKyc: {
    data: EmptyFetchable,
    pagination: {},
  },
  allSelfKyc: {
    data: EmptyFetchable,
    pagination: {},
  },
  allKyb: {
    data: EmptyFetchable,
    pagination: {},
  },
  allIndividualAml: {
    data: EmptyFetchable,
    pagination: {},
  },
  allBusinessAml: {
    data: EmptyFetchable,
    pagination: {},
  },
  allDueDiligenceConfig: {
    data: EmptyFetchable,
    pagination: {},
  },
  newDueDiligenceForm: {},
};
// Reducer
export const verificationReducer: Reducer<
  Verification,
  VerificationActions.Actions
> = createReducer(initialState, (builder) => {
  builder
    .addCase(VerificationActions.getAllKyb, (state) => {
      state.allKyb.data = {
        status: "loading",
      };
    })
    .addCase(VerificationActions.setAllKybPagination, (state, action) => {
      state.allKyb.pagination = action.payload;
      state.allKyb.data = {
        status: "loading",
      };
    })
    .addCase(VerificationActions.setAllKyb, (state, action) => {
      const newPayload = transformActionWithCountry(action.payload);
      state.allKyb.data = newPayload;
    })
    .addCase(VerificationActions.resetAllKyb, (state) => {
      state.allKyb.data = EmptyFetchable;
      state.allKyb.pagination = {};
    })
    .addCase(VerificationActions.getAllSelfKyc, (state) => {
      state.allSelfKyc.data = {
        status: "loading",
      };
    })
    .addCase(VerificationActions.setAllSelfKycPagination, (state, action) => {
      state.allSelfKyc.pagination = action.payload;
      state.allSelfKyc.data = {
        status: "loading",
      };
    })
    .addCase(VerificationActions.setAllSelfKyc, (state, action) => {
      const newPayload = produce(action.payload, (p) => {
        if (p.status !== "ready") return;
        p.data.content.forEach((item) => {
          if (item.user.address && item.user.address.country) {
            item.user.address.country = item.user.address.country.toUpperCase() as typeof countries.T;
          }
        });
      });
      state.allSelfKyc.data = newPayload;
    })
    .addCase(VerificationActions.resetAllSelfKyc, (state) => {
      state.allSelfKyc.data = EmptyFetchable;
      state.allSelfKyc.pagination = {};
    })
    .addCase(VerificationActions.getAllKyc, (state) => {
      state.allKyc.data = {
        status: "loading",
      };
    })
    .addCase(VerificationActions.setAllKycPagination, (state, action) => {
      state.allKyc.pagination = action.payload;
      state.allKyc.data = {
        status: "loading",
      };
    })
    .addCase(VerificationActions.setAllKyc, (state, action) => {
      const newPayload = produce(action.payload, (p) => {
        if (p.status !== "ready") return;
        p.data.content.forEach((item) => {
          if (item.user.address && item.user.address.country) {
            item.user.address.country = item.user.address.country.toUpperCase() as typeof countries.T;
          }
        });
      });

      state.allKyc.data = newPayload;
    })
    .addCase(VerificationActions.resetAllKyc, (state) => {
      state.allKyc.data = EmptyFetchable;
      state.allKyc.pagination = {};
    })
    .addCase(VerificationActions.getAllIndividualAml, (state) => {
      state.allIndividualAml.data = {
        status: "loading",
      };
    })
    .addCase(
      VerificationActions.setAllIndividualAmlPagination,
      (state, action) => {
        state.allIndividualAml.pagination = action.payload;
        state.allIndividualAml.data = {
          status: "loading",
        };
      }
    )
    .addCase(VerificationActions.setAllIndividualAml, (state, action) => {
      state.allIndividualAml.data = action.payload;
    })
    .addCase(VerificationActions.resetAllIndividualAml, (state) => {
      state.allIndividualAml.data = EmptyFetchable;
      state.allIndividualAml.pagination = {};
    })
    .addCase(VerificationActions.getAllBusinessAml, (state) => {
      state.allBusinessAml.data = {
        status: "loading",
      };
    })
    .addCase(
      VerificationActions.setAllBusinessAmlPagination,
      (state, action) => {
        state.allBusinessAml.pagination = action.payload;
        state.allBusinessAml.data = {
          status: "loading",
        };
      }
    )
    .addCase(VerificationActions.setAllBusinessAml, (state, action) => {
      state.allBusinessAml.data = action.payload;
    })
    .addCase(VerificationActions.resetAllBusinessAml, (state) => {
      state.allBusinessAml.data = EmptyFetchable;
      state.allBusinessAml.pagination = {};
    })
    .addCase(VerificationActions.resetChecksSummary, (state) => {
      state.checksSummary = EmptyFetchable;
    })
    .addCase(VerificationActions.getChecksSummary, (state) => {
      state.checksSummary = {
        status: "loading",
      };
    })
    .addCase(VerificationActions.setChecksSummary, (state, action) => {
      state.checksSummary = action.payload;
    })
    // .addCase(VerificationActions.resetKYBJurisdictions, (state) => {
    //   state.KYBJurisdictions = EmptyFetchable;
    // })
    // .addCase(VerificationActions.getKYBJurisdictions, (state) => {
    //   state.KYBJurisdictions = {
    //     status: "loading",
    //   };
    // })
    // .addCase(VerificationActions.setKYBJurisdictions, (state, action) => {
    //   state.KYBJurisdictions = action.payload;
    // })
    .addCase(VerificationActions.getAllDueDiligenceConfig, (state) => {
      state.allDueDiligenceConfig.data = {
        status: "loading",
      };
    })
    .addCase(
      VerificationActions.setAllDueDiligenceConfigPagination,
      (state, action) => {
        state.allDueDiligenceConfig.pagination = action.payload;
        state.allDueDiligenceConfig.data = {
          status: "loading",
        };
      }
    )
    .addCase(VerificationActions.setAllDueDiligenceConfig, (state, action) => {
      state.allDueDiligenceConfig.data = action.payload;
    })
    .addCase(VerificationActions.resetAllDueDiligenceConfig, (state) => {
      state.allDueDiligenceConfig.data = EmptyFetchable;
      state.allDueDiligenceConfig.pagination = {};
    })
    .addCase(VerificationActions.setCurrentMerchantConfig, (state, action) => {
      if (isFetchableError(action.payload)) {
        state.currentMerchant = action.payload;
      } else {
        state.currentMerchant = {
          status: "ready",
          data: action.payload.data,
        };
      }
    })
    .addCase(VerificationActions.resetCurrentMerchantConfig, (state) => {
      state.currentMerchant = EmptyFetchable;
    })
    .addCase(VerificationActions.getCurrentMerchantConfig, (state) => {
      state.currentMerchant = {
        status: "loading",
      };
    });
});
